import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod/v4";

import { api } from "@/lib/api-client";
import { getGarageCarsQueryOptions } from "@/services/cars/get-garage-cars";

import type { MutationConfig } from "@/lib/react-query";
import type { Car } from "@/types/car";

export const editCarSchema = z.object({
  nickname: z.string().optional(),
  owner: z.string().nonempty(),
  purchase_price: z.number().positive("Price must be greater than 0"),
  purchase_date: z.string().nonempty(),
  licence_plate: z.string().optional(),
  country_of_registration: z.string().optional(),
  color: z.string(),
  interior_colors: z.string(),
  condition: z.enum(["superb", "excellent", "good", "fair", ""]).optional(),
  number_of_previous_owner: z.number().optional(),
  special_equipment: z.string().optional(),
  vin: z.string().optional(),
});

export type UpdateCarInput = Omit<z.infer<typeof editCarSchema>, "status">;

export const updateCar = async ({
  data,
  carId,
}: {
  data: UpdateCarInput;
  carId: string;
  garageId?: string;
}): Promise<Car> => {
  return (await api.patch(`/carburate/cars/${carId}`, data)) as Car;
};

type UseUpdateCarOptions = {
  mutationConfig?: MutationConfig<typeof updateCar>;
};

export const useUpdateCar = ({ mutationConfig }: UseUpdateCarOptions = {}) => {
  const queryClient = useQueryClient();

  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    onSuccess: (data, vars, ...args) => {
      queryClient.invalidateQueries({ queryKey: ["cars", vars.carId] });

      if (vars.garageId) {
        queryClient.invalidateQueries({
          queryKey: getGarageCarsQueryOptions({
            garageId: vars.garageId,
            page: 1,
            pageSize: 999999,
          }).queryKey,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["garage-market-car-ids"] });
      // Refresh portfolio totals in case update affects counts/status
      queryClient.invalidateQueries({ queryKey: ["portfolio"] });
      onSuccess?.(data, vars, ...args);
    },
    ...restConfig,
    mutationFn: updateCar,
  });
};
